/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import {
	useState,
	useEffect,
	useLayoutEffect,
} from 'react';

// Mantine
import { Box, Text, Image, Stack } from '@mantine/core';

// Components
import { Button } from '@components/atoms/Button';
import { NumericInput } from '@components/atoms/NumericInput';

// Styles
import classes from './SelectionCart.module.css';
import clsx from 'clsx';

// Icons
import NoSelectedItemsCartIcon from '@assets/icons/no-selected-items-cart.svg';
import CarbonIcon from '@assets/icons/carbon.svg';
import PocketMoneyIcon from '@assets/icons/pocket-money.svg';

// Utils
import {
	sum20Percent,
	truncateString,
} from '@utils/functions';
import { getUnitLabel } from '@utils/units';

// Store
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@components/atoms/Tooltip';
import {
	formatCurrency,
	formatTwoDecimals,
	formatTokenAvailable,
	formatNumberWithSpaces,
} from '@utils/formats';
import { getCurrencyUnit } from '@utils/currency-units';
import Decimal from 'decimal.js';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const SelectionCart = (props: {
	size: string;
	title: string;
	type: string;
	max: string;
	price?: number;
	isLoading?: boolean;
	onRemove: (_item: any) => void;
	handlechangequantity: (
		_item: any,
		_qte: number
	) => void;
	handlechangeprice: (_item: any, _price: number) => void;
	handleConfirm?: () => void;
	setError?: (isError: boolean) => void;
	items: any[];
	onchangestatus?: (isEnabled: boolean) => void;
}) => {
	/* ********************************** Hooks ********************************* */

	const [isOverMaxQuantity, setIsOverMaxQuantity] =
		useState(false);

	const [isOverMaxPrice, setIsOverMaxPrice] =
		useState(false);

	const { t } = useTranslation();

	/* ******************************** HELPER ********************************** */
	const IS_SMALL = props.size === 'sm';

	const getNoSelectedItems = () => {
		return (
			<Box className={classes.containerNoSelectedItems}>
				<Image
					className={clsx(
						classes.iconNoSelectedItems,
						IS_SMALL && classes.iconNoSelectedItemsSm
					)}
					src={NoSelectedItemsCartIcon}
				/>
				<Text
					className={clsx(
						classes.textNoSelectedItems,
						IS_SMALL && classes.textNoSelectedItemsSm
					)}
				>
					{t('global.noSelectedProject')}
				</Text>
			</Box>
		);
	};

	const totalTco2eq =
		props.items && Array.isArray(props.items)
			? props.items.reduce((total: number, item: any) => {
					const _total = new Decimal(total);
					const _quantitytco2eq = new Decimal(
						Number(item.quantitytco2eq)
					);
					return Decimal.sum(
						_total,
						_quantitytco2eq
					).toNumber();
				}, 0)
			: 0;

	const totalPricesMesurabilityProjects =
		props.items && Array.isArray(props.items)
			? props.items.reduce(
					(total: number, item: any) =>
						total +
						item.quantitytco2eq *
							item.sellPrice_europertco2eq,
					0
				)
			: 0;

	const totalPricesAdditionalProjects =
		props.items && Array.isArray(props.items)
			? props.items.reduce(
					(total: number, item: any) =>
						total + item.price,
					0
				)
			: 0;

	const averagePrices = props.max
		? totalPricesMesurabilityProjects /
			parseFloat(props.max)
		: 0;

	useLayoutEffect(() => {
		const numericTotalTco2eq = new Decimal(
			Number(totalTco2eq).toFixed(3)
		).toNumber();
		const numericMax = new Decimal(
			Number(props.max).toFixed(3)
		).toNumber();
		if (
			numericTotalTco2eq >= 0 &&
			numericTotalTco2eq !== numericMax
		) {
			setIsOverMaxQuantity(true);
			props.setError && props.setError(true);
		} else if (numericTotalTco2eq == numericMax) {
			setIsOverMaxQuantity(false);
			props.setError && props.setError(false);
		}
	}, [totalTco2eq]);

	useLayoutEffect(() => {
		if (
			props.price &&
			totalPricesAdditionalProjects >= 0 &&
			totalPricesAdditionalProjects !== props.price
		) {
			setIsOverMaxPrice(true);
			props.setError && props.setError(true);
		} else if (
			totalPricesAdditionalProjects == props.price
		) {
			setIsOverMaxPrice(false);
			props.setError && props.setError(false);
		}
	}, [totalPricesAdditionalProjects, props.price]);

	const getTotal = () => {
		switch (props.type) {
			case 'carbon':
				return (
					<>
						<Box className={classes.rowSubtotal}>
							<Text
								className={clsx(
									classes.labelSubtotal,
									IS_SMALL && classes.labelSubtotalSm
								)}
							>
								{t('global.total')}{' '}
								{getUnitLabel('tco2eq')}
							</Text>
							<Box className={classes.containerItemToken}>
								<Text
									className={clsx(
										classes.valueSubtotal,
										IS_SMALL && classes.valueSubtotalSm,
										isOverMaxQuantity && classes.textError
									)}
								>
									{formatTwoDecimals(Number(totalTco2eq))}
								</Text>
								<Text
									className={clsx(
										classes.valueSubtotalUnit,
										IS_SMALL &&
											classes.valueSubtotalUnitSm
									)}
								>
									/ {formatTwoDecimals(Number(props.max))}
								</Text>
							</Box>
						</Box>
						<Box
							className={clsx(
								classes.spacerSubtotal,
								IS_SMALL && classes.spacerSubtotalSm
							)}
						/>
						<Box className={classes.rowSubtotal}>
							<Text
								className={clsx(
									classes.labelSubtotal,
									IS_SMALL && classes.labelSubtotalSm
								)}
							>
								{t('global.total')} {t('global.exclVAT')}
							</Text>
							<Text
								className={clsx(
									classes.valueSubtotal,
									IS_SMALL && classes.valueSubtotalSm
								)}
							>
								{formatCurrency(
									totalPricesMesurabilityProjects,
									getCurrencyUnit('EUR')
								)}
							</Text>
						</Box>
					</>
				);

			case 'prices':
				return (
					<>
						<Box className={classes.rowSubtotal}>
							<Text
								className={clsx(
									classes.labelSubtotal,
									IS_SMALL && classes.labelSubtotalSm
								)}
							>
								{t('global.total')}
							</Text>
							<Box className={classes.containerItemToken}>
								<Text
									className={clsx(
										classes.valueSubtotal,
										IS_SMALL && classes.valueSubtotalSm,
										isOverMaxPrice && classes.textError
									)}
								>
									{formatCurrency(
										Number(totalPricesAdditionalProjects),
										''
									)}
								</Text>
								<Text
									className={clsx(
										classes.valueSubtotalUnit,
										IS_SMALL &&
											classes.valueSubtotalUnitSm
									)}
								>
									/
									{props.price &&
										formatCurrency(
											Number(props.price),
											getCurrencyUnit('EUR')
										)}
								</Text>
							</Box>
						</Box>
					</>
				);

			default:
				return (
					<Stack>
						<Box className={classes.rowSubtotal}>
							<Text
								className={clsx(
									classes.labelAverage,
									IS_SMALL && classes.labelAverageSm
								)}
							>
								{t('global.averagePriceExclVat')}
							</Text>
							<Box className={classes.containerItemToken}>
								<Text
									className={clsx(
										classes.valueAverage,
										IS_SMALL && classes.valueAverageSm
									)}
								>
									{formatCurrency(
										Number(averagePrices),
										getCurrencyUnit('EURperTonne')
									)}
								</Text>
							</Box>
						</Box>
						<Box className={classes.rowSubtotal}>
							<Text
								className={clsx(
									classes.labelTotalHt,
									IS_SMALL && classes.labelTotalHtSm
								)}
							>
								{t('global.totalPriceExclVat')}
							</Text>
							<Box className={classes.containerItemToken}>
								<Text
									className={clsx(
										classes.valueTotalHt,
										IS_SMALL && classes.valueTotalHtSm
									)}
								>
									{formatCurrency(
										Number(
											totalPricesMesurabilityProjects
										),
										getCurrencyUnit('EUR')
									)}
								</Text>
							</Box>
						</Box>
						<Box className={classes.rowSubtotal}>
							<Text
								className={clsx(
									classes.labelTotalPrice,
									IS_SMALL && classes.labelTotalPriceSm
								)}
							>
								{t('global.totalPriceInclVat')}
							</Text>
							<Box className={classes.containerItemToken}>
								<Text
									className={clsx(
										classes.valueTotalPrice,

										IS_SMALL && classes.valueTotalPriceSm
									)}
								>
									{formatCurrency(
										sum20Percent(
											Number(
												totalPricesMesurabilityProjects
											)
										),
										getCurrencyUnit('EUR')
									)}
								</Text>
							</Box>
						</Box>
					</Stack>
				);
		}
	};

	const getSubtotal = () => {
		return (
			<Box
				className={clsx(
					classes.containerSubtotal,
					props.type == 'summary' &&
						classes.containerSubtotalSummary
				)}
			>
				{getTotal()}
			</Box>
		);
	};

	const getIcon = () => {
		switch (props.type) {
			case 'carbon':
				return CarbonIcon;

			case 'prices':
				return PocketMoneyIcon;

			default:
				return CarbonIcon;
		}
	};

	const getUnit = () => {
		switch (props.type) {
			case 'carbon':
				return 'tonnes';
			case 'prices':
				return null;
			default:
				return 'tonnes';
		}
	};

	const getDescriptionUnit = () => {
		switch (props.type) {
			case 'carbon':
				return t('offsets.offsetQuantity');

			case 'prices':
				return t('offsets.additionalityTotal');

			default:
				return t('offsets.offsetQuantity');
		}
	};

	const getConfirmationBtnText = () => {
		switch (props.type) {
			case 'carbon':
				return t('global.next');
			case 'prices':
				return t('global.next');
			default:
				return t('global.offset');
		}
	};

	const isQuantityZero = (arrayOfObjects: any) =>
		arrayOfObjects.some(
			(item: any) => item.quantitytco2eq === 0
		);
	const isPriceZero = (arrayOfObjects: any) =>
		arrayOfObjects.some((item: any) => item.price === 0);

	useEffect(() => {
		if (props.onchangestatus) {
			const isDisabled =
				props.type == 'carbon'
					? isOverMaxQuantity ||
						totalTco2eq == 0 ||
						isQuantityZero(props.items)
					: isOverMaxPrice ||
						props.items.length == 0 ||
						isPriceZero(props.items);

			props.onchangestatus(!isDisabled);
		}
	}, [
		props.type == 'carbon'
			? isOverMaxQuantity ||
				totalTco2eq == 0 ||
				isQuantityZero(props.items)
			: isOverMaxPrice ||
				props.items.length == 0 ||
				isPriceZero(props.items),
	]);

	/* ******************************** RENDERING ******************************* */
	return (
		<Box
			className={clsx(
				classes.container,
				IS_SMALL &&
					props.type !== 'summary' &&
					classes.containerSm,
				props.type == 'summary' &&
					classes.containerSummary,
				props.type == 'summary' &&
					IS_SMALL &&
					classes.containerSummarySm
			)}
		>
			{/** Header */}
			<Box
				className={clsx(
					classes.header,
					props.type == 'summary' && classes.headerSummary
				)}
			>
				<Text
					className={clsx(
						classes.title,
						IS_SMALL && classes.titleSm
					)}
				>
					{props.title}
				</Text>
				<Box className={classes.containerRightHeader}>
					<Image
						className={clsx(
							classes.iconHeader,
							IS_SMALL && classes.iconHeaderSm
						)}
						src={getIcon()}
					/>
					<Box
						className={classes.containerRightHeaderTexts}
					>
						<Box className={classes.containerValue}>
							<Text
								className={clsx(
									classes.rightHeaderValue,
									IS_SMALL && classes.rightHeaderValueSm
								)}
							>
								{props.type !== 'carbon' &&
									props.type !== 'summary' &&
									formatNumberWithSpaces(
										Number(props.price)
									)}
								{props.type == 'summary' && props.max}
								{props.type == 'carbon' &&
									formatTwoDecimals(Number(props.max))}
								{props.type !== 'carbon' &&
									props.type !== 'summary' &&
									'€'}
							</Text>
							<Text
								className={clsx(
									classes.rightHeaderUnit,
									IS_SMALL && classes.rightHeaderUnitSm
								)}
							>
								{getUnit()}
							</Text>
						</Box>
						<Text
							className={clsx(
								classes.rightHeaderSubtitle,
								IS_SMALL && classes.rightHeaderSubtitleSm
							)}
						>
							{getDescriptionUnit()}
						</Text>
					</Box>
				</Box>
			</Box>
			{/** Body */}
			<Box
				className={clsx(
					classes.body,
					props.type == 'summary' && classes.bodySummary
				)}
			>
				{props.items.length == 0 && getNoSelectedItems()}
				{props.items.length !== 0 &&
					props.type !== 'summary' && (
						<Box
							className={classes.containerSelectedItems}
						>
							{props.items
								.slice(0, 4)
								.map((project: any) => (
									<Box
										key={project.id}
										className={
											classes.containerSelectedItem
										}
									>
										<Box
											className={
												classes.containerSelectedItemLeft
											}
										>
											<Image
												radius={'md'}
												className={clsx(
													classes.itemImage,
													IS_SMALL && classes.itemImageSm
												)}
												src={project.image}
											/>
											<Box>
												<Tooltip
													isActive={
														project?.name &&
														project.name.length > 18
													}
													label={project.name}
												>
													<Text
														className={clsx(
															classes.itemTitle,
															IS_SMALL &&
																classes.itemTitleSm
														)}
													>
														{truncateString(
															project.name,
															18
														)}
													</Text>
												</Tooltip>
												{props.type == 'carbon' && (
													<Box
														className={
															classes.containerItemToken
														}
													>
														<Text
															className={clsx(
																classes.itemToken,
																IS_SMALL &&
																	classes.itemTokenSm
															)}
														>
															{formatTokenAvailable(
																Number(
																	project.tokenAvailable
																)
															)}
														</Text>

														<Text
															className={clsx(
																classes.itemTokenUnit,
																IS_SMALL &&
																	classes.itemTokenUnitSm
															)}
														>
															(
															{t('global.availableStock')}
															)
														</Text>
													</Box>
												)}
												{props.type == 'carbon' && (
													<Box
														className={
															classes.containerItemPrice
														}
													>
														<Text
															className={clsx(
																classes.itemPrice,
																IS_SMALL &&
																	classes.itemPriceSm
															)}
														>
															{project.priceTco2}
														</Text>
														<Text
															className={clsx(
																classes.itemTokenUnit,
																IS_SMALL &&
																	classes.itemTokenUnitSm
															)}
														>
															/{getUnitLabel('tco2eq')}
														</Text>
													</Box>
												)}
												{props.type == 'prices' && (
													<Box
														className={
															classes.containerItemToken
														}
													>
														<Text
															className={clsx(
																classes.itemToken,
																IS_SMALL &&
																	classes.itemTokenSm
															)}
														>
															{project.coverage}
														</Text>

														<Text
															className={clsx(
																classes.coverageText
															)}
														>
															({t('global.coverage')})
														</Text>
													</Box>
												)}
											</Box>
										</Box>
										<Box
											className={
												classes.containerSelectedItemRight
											}
										>
											<Box
												className={clsx(
													classes.containerTco2,
													IS_SMALL &&
														classes.containerTco2Sm
												)}
											>
												<NumericInput
													data-testid="M00kG199_Z5jdHi6B-input"
													decimalScale={
														props.type == 'carbon' ? 3 : 2
													}
													max={
														props.type == 'carbon'
															? Number(
																	project.tokenAvailable
																)
															: project.financialNeeds !=
																  'Unlimited'
																? Number(
																		project.financialNeeds
																	) -
																	Number(project.coverage)
																: undefined
													}
													allowDecimal={
														props.type == 'carbon'
													}
													unit={
														props.type == 'carbon'
															? getUnitLabel('tco2')
															: '€'
													}
													size={IS_SMALL ? 'xs' : 'sm'}
													value={
														props.type == 'prices'
															? project.price
															: project.quantitytco2eq
													}
													onChange={(_value: number) => {
														if (props.type == 'prices') {
															props.handlechangeprice(
																project,
																_value
															);
														} else {
															props.handlechangequantity(
																project,
																_value
															);
														}
													}}
												/>
											</Box>
											<Box
												className={clsx(
													classes.customDelete,
													IS_SMALL &&
														classes.customDeleteSm
												)}
												onClick={() => {
													props.onRemove(project);
												}}
											>
												{t('global.delete')}
											</Box>
										</Box>
									</Box>
								))}
						</Box>
					)}
				{props.items.length !== 0 && getSubtotal()}
			</Box>
			{/** Buttons */}
			<Box className={classes.buttons}>
				<Button
					loading={
						props.isLoading ? props.isLoading : false
					}
					onClick={props.handleConfirm}
					size={IS_SMALL ? 'sm' : 'md'}
					fullWidth
					disabled={
						props.type == 'carbon'
							? isOverMaxQuantity ||
								totalTco2eq == 0 ||
								isQuantityZero(props.items)
							: isOverMaxPrice ||
								props.items.length == 0 ||
								isPriceZero(props.items)
					}
				>
					{getConfirmationBtnText()}
				</Button>

				{props.type == 'carbon' &&
				isOverMaxQuantity &&
				props.items.length > 0 ? (
					<Text className={clsx(classes.textAlert)}>
						{t('offsets.textOverMaxQuantity')}
					</Text>
				) : null}
				{props.type == 'prices' &&
				isOverMaxPrice &&
				props.items.length > 0 ? (
					<Text className={clsx(classes.textAlert)}>
						{t('offsets.textOverMaxPrice')}
					</Text>
				) : null}
			</Box>
		</Box>
	);
};
